import * as React from "react";
import FundsService from "@services/Funds.service";
import { Button, Space, Spin, Table, TableColumnsType, Tooltip } from "antd";
import CurrencyElement from "@appComponents/CurrencyElement/CurrencyElement";
import { classNames } from "@features/utils/reactFunctions";
import { Link } from "react-router-dom";
import { FUNDS_UNSTAKING_FORM } from "@funds/config";
import Big from "big.js";
import { isTier1AndAbove } from "@funds/features/helpers/functions";
import ConditionalWrapper from "@appComponents/ConditionalWrapper";
import { tradeUnstakeRestrictionMessage } from "@funds/features/helpers/validationMessages";

import * as styles from "./CurrencyDetailsStyles.module.scss";
import * as fundsStyle from "../../../../../../FundsAppStyle.module.scss";
type Props = {
  currency: string;
  investorId: number;
  isUk?: boolean;
  tier: string;
};

export type StakingEthResponse = {
  [index: string]: string | number;
  active_staked_eth: number;
  timelocked_staked_eth: number;
  deposited_eth: number;
  pending_staked_eth: number;
  rewards_eth: number;
  available_staked_eth: number;
};

const DataForCurrency: Record<string, string[]> = {
  eth: ["deposited_eth", "rewards_eth"],
  staked_eth: [
    "available_staked_eth",
    "timelocked_staked_eth",
    "pending_staked_eth",
  ],
};

const LabelMapper: Record<string, string> = {
  deposited_eth: "Deposited",
  rewards_eth: "Staking Rewards",
  available_staked_eth: "Active Available",
  pending_staked_eth: "In Queue",
  timelocked_staked_eth: "Active timelocked",
};

const CurrencyDetails = ({ currency, investorId, isUk, tier }: Props) => {
  const [currencyDetails, setCurrencyDetails] =
    React.useState<StakingEthResponse>();

  React.useEffect(() => {
    FundsService.getStakingEth(investorId).then(
      (resp: { data: StakingEthResponse }) => {
        setCurrencyDetails(resp.data);
      }
    );
  }, []);

  if (!currencyDetails) return <Spin />;

  const renderTable = () => {
    const columns: TableColumnsType<any> = [
      {
        dataIndex: "currencyName",
        key: "currencyName",
        width: "27%",
        render: (row, value) => (
          <div className={styles.currencyName}>
            <span className={classNames(styles[value.key], styles.badge)}>
              {LabelMapper[value.key]}
            </span>
          </div>
        ),
      },
      {
        dataIndex: "value",
        key: "value",
        width: "63%",
        render: (row, value) => (
          <CurrencyElement value={value.value} currency={currency} />
        ),
      },
      {
        dataIndex: "actions",
        key: "actions",
        width: "63%",
        render: (value, record) => {
          const unstakingEnabled = process.env.UNSTAKING_ENABLED;
          const keysWithUnstakeBtn = [
            "available_staked_eth",
            "timelocked_staked_eth",
          ];

          const isDisabled =
            Big(record.value).eq(0) || record.key === "timelocked_staked_eth";

          if (unstakingEnabled && keysWithUnstakeBtn.includes(record.key)) {
            return (
              <Space wrap>
                <ConditionalWrapper
                  condition={!isDisabled && isTier1AndAbove(tier)}
                  wrapper={(childern) => (
                    <Tooltip title={tradeUnstakeRestrictionMessage}>
                      {childern}
                    </Tooltip>
                  )}
                >
                  <Link
                    to={isDisabled ? "#" : `${FUNDS_UNSTAKING_FORM}`}
                    className={classNames(
                      fundsStyle.button,
                      !isUk ? fundsStyle.buttonPurple : undefined,
                      isDisabled && !isUk
                        ? fundsStyle.buttonPurpleDisabled
                        : undefined
                    )}
                  >
                    <Button
                      disabled={(isDisabled && isUk) || isTier1AndAbove(tier)}
                    >
                      Unstake
                    </Button>
                  </Link>
                </ConditionalWrapper>
              </Space>
            );
          }

          return null;
        },
      },
    ];

    const data = DataForCurrency[currency].map((key: string) => ({
      key: key,
      value: currencyDetails[key],
    }));

    return (
      <Table
        columns={columns}
        showHeader={false}
        dataSource={data}
        pagination={false}
      />
    );
  };
  return <div>{renderTable()}</div>;
};

export default CurrencyDetails;
